import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { getApiErrorMessage, shouldRetryRequest } from "api/apiError";
import {
  fetchUserLoginHistory,
  userKeys,
  type LoginHistoryEntry,
  type LoginLocation,
} from "api/users";
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  EmptyState,
  ErrorState,
} from "Components/ui";
import { History } from "Components/ui/icons";

interface LoginHistoryModalProps {
  open: boolean;
  userId: string | null;
  /** Shown in the description. */
  userName?: string;
  onClose: () => void;
}

const formatIPAddress = (ip?: string | null): string => {
  const trimmed = (ip || "").trim();
  if (!trimmed) {
    return "Unknown";
  }
  const stripped = trimmed.replace(/^::ffff:/, "");
  if (trimmed === "::1" || stripped === "127.0.0.1") {
    return "Localhost";
  }
  return stripped;
};

const formatLocation = (
  location: LoginLocation | undefined,
  ip?: string | null
): { primary: string; secondary: string | null } => {
  if (!location) {
    const ipLabel = formatIPAddress(ip);
    return {
      primary: ipLabel !== "Unknown" ? ipLabel : "Location not detected",
      secondary: null,
    };
  }
  if (typeof location === "string") {
    const trimmed = location.trim();
    if (!trimmed) {
      return { primary: "Location not detected", secondary: null };
    }
    const [primary, ...rest] = trimmed.split(" · ");
    return { primary: primary || trimmed, secondary: rest.length > 0 ? rest.join(" · ") : null };
  }
  const parts = [location.label, location.city, location.region, location.country].filter(Boolean);
  const { lat, lng } = location;
  return {
    primary: parts.length > 0 ? parts.join(", ") : "Location not detected",
    secondary:
      typeof lat === "number" && typeof lng === "number"
        ? `${lat.toFixed(4)}, ${lng.toFixed(4)}`
        : null,
  };
};

const isFailed = (entry: LoginHistoryEntry): boolean => {
  const status = entry.status?.toLowerCase();
  return status === "failed" || entry.success === false || (!entry.ip && entry.success !== true);
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? "Unknown time" : date.toLocaleString();
};

const columns: ColumnDef<LoginHistoryEntry>[] = [
  {
    id: "when",
    header: "When",
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatTimestamp(row.original.timestamp)}
      </span>
    ),
  },
  {
    id: "location",
    header: "Location",
    cell: ({ row }) => {
      const { primary, secondary } = formatLocation(row.original.location, row.original.ip);
      return (
        <div className="min-w-0 py-2">
          <p className="text-foreground">{primary}</p>
          {secondary && <p className="text-xs text-muted-foreground">{secondary}</p>}
        </div>
      );
    },
  },
  {
    id: "ip",
    header: "IP address",
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {formatIPAddress(row.original.ip)}
      </span>
    ),
  },
  {
    id: "status",
    header: "Result",
    meta: { align: "right" },
    cell: ({ row }) =>
      isFailed(row.original) ? (
        <Badge tone="danger" dot>
          Failed
        </Badge>
      ) : (
        <Badge tone="success" dot>
          {row.original.status || "Success"}
        </Badge>
      ),
  },
];

/** A user's recent sign-ins, newest first (GET /api/rbac/users/:id/logins). */
const LoginHistoryModal: React.FC<LoginHistoryModalProps> = ({
  open,
  userId,
  userName,
  onClose,
}) => {
  const isOpen = open && Boolean(userId);
  const history = useQuery({
    queryKey: userKeys.logins(userId || ""),
    queryFn: () => fetchUserLoginHistory(userId || ""),
    enabled: isOpen,
    retry: shouldRetryRequest,
  });

  const entries = React.useMemo(
    () =>
      [...(history.data || [])].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    [history.data]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent size="xl">
        <DialogHeader
          title="Login history"
          description={`Sign-ins for ${userName || "this user"}, newest first.`}
        />
        <DialogBody>
          {history.isError ? (
            <ErrorState
              title="Login history didn't load"
              description={getApiErrorMessage(history.error)}
              action={
                <Button variant="secondary" onClick={() => history.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : (
            <DataTable
              columns={columns}
              data={entries}
              loading={history.isInitialLoading}
              enableSorting={false}
              density="compact"
              caption="Login history"
              getRowId={(entry, index) => `${entry.timestamp}-${index}`}
              empty={
                <EmptyState
                  compact
                  icon={<History />}
                  title="No sign-ins yet"
                  description="Sign-ins show up here once this user logs in."
                />
              }
            />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default LoginHistoryModal;
